import {
  ECOURTS_LOOKUP_DEADLINE_MS,
  ECOURTS_LOOKUP_TARGET_MS,
  ECOURTS_MAX_CONCURRENCY,
  type EcourtsHealth,
  type EcourtsRuntimeMonitor,
  type EcourtsRuntimeStatus
} from "../../../packages/core/src/ecourts-performance.js";
import { escapeAttr, escapeHtml } from "./dashboard-html.js";
import { formatInteger, formatNumber } from "./format.js";

const UNMEASURED = "unmeasured";
const MONITOR_TIMEZONE = "Asia/Makassar";
const DEFAULT_WINDOW_MS = 15 * 60_000;
const DEFAULT_MAX_SAMPLES = 100;

const MONITOR_OPEN_KEY = "pipeline-ecourts-monitor-open";

export function ecourtsMonitorOpenFromStorage(): boolean {
  try {
    return globalThis.localStorage?.getItem(MONITOR_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function persistEcourtsMonitorOpen(open: boolean): void {
  try {
    globalThis.localStorage?.setItem(MONITOR_OPEN_KEY, open ? "1" : "0");
  } catch {
    // Storage is optional; collapsed-by-default still holds.
  }
}

export function ecourtsMonitorHtml(
  health: EcourtsHealth | null | undefined,
  options: { open?: boolean } = {}
): string {
  const runtime = health?.runtime ?? null;
  const issues = monitorIssues(health);
  const status = runtime ? statusCopy(runtime, issues) : "Unavailable";
  const tone = monitorTone(runtime?.status, issues.length === 0);
  const active = runtime?.active ?? [];
  const activeLabel = runtime ? `${formatInteger(runtime.concurrency)}/${ECOURTS_MAX_CONCURRENCY}` : UNMEASURED;
  const queueNote = health
    ? `queued ${formatInteger(health.queued)} · running ${formatInteger(health.running)}`
    : UNMEASURED;
  const p95 = runtime ? durationMs(runtime.performance.p95Ms) : UNMEASURED;
  const windowNote = rollingWindowNote(runtime);
  const p95State = runtime?.performance.p95Ms == null
    ? UNMEASURED
    : runtime.performance.p95Ms <= ECOURTS_LOOKUP_TARGET_MS ? "on target" : "over target";
  const completed = runtime
    ? formatInteger(runtime.performance.completedPerMinute)
    : UNMEASURED;
  const completedNote = runtime
    ? `last 1 min · failures ${formatInteger(runtime.performance.failures)} · timeouts ${formatInteger(runtime.performance.timeouts)} in ${windowNote}`
    : UNMEASURED;
  const activeHtml = active.length
    ? `<ul class="ecourts-monitor-active" aria-label="active eCourts cases">${active.map((attempt) => {
        const stalled = attempt.progressAgeMs >= 20_000 || attempt.elapsedMs >= ECOURTS_LOOKUP_DEADLINE_MS;
        return `<li class="${stalled ? "is-alert" : ""}">
          <span><strong>${escapeHtml(attempt.caseNumber || attempt.runId)}</strong> · ${escapeHtml(attempt.stage || "stage unavailable")}</span>
          <small>${durationMs(attempt.elapsedMs)} elapsed · ${durationMs(attempt.progressAgeMs)} since progress${attempt.elapsedMs >= ECOURTS_LOOKUP_DEADLINE_MS ? " · 60s cap exceeded" : ""}</small>
        </li>`;
      }).join("")}</ul>`
    : `<p class="ecourts-monitor-empty">${runtime ? "No active cases." : "Active cases are unmeasured."}</p>`;
  const issueHtml = issues.length
    ? `<div class="ecourts-monitor-alerts" role="alert"><strong>Needs attention</strong><ul>${issues.map((issue) => `<li>${escapeHtml(issueCopy(issue))}</li>`).join("")}</ul>${recoveryHtml(issues, runtime)}</div>`
    : "";
  const open = options.open === true;
  return `
    <details class="ecourts-monitor" data-ecourts-monitor data-state="${escapeAttr(tone)}" data-ecourts-status="${escapeAttr(runtime?.status ?? "unavailable")}" aria-label="eCourts runtime monitor"${open ? " open" : ""}>
      <summary class="ecourts-monitor-head">
        <div>
          <h2>eCourts runtime</h2>
          <p>4 parallel lookups · ${formatInteger(ECOURTS_LOOKUP_TARGET_MS / 1000)}s target · ${formatInteger(ECOURTS_LOOKUP_DEADLINE_MS / 1000)}s cap · VPS browser workers</p>
        </div>
        <span class="live-badge ${tone}">${escapeHtml(status)}</span>
      </summary>
      <div class="ecourts-monitor-body">
      <div class="ecourts-monitor-grid">
        ${monitorMetric("Active", activeLabel, queueNote)}
        ${monitorMetric("Browser", browserLabel(runtime), extensionLabel(runtime))}
        ${monitorMetric("P95", p95, `${p95State} · target ${formatInteger(ECOURTS_LOOKUP_TARGET_MS / 1000)}s · ${windowNote}`)}
        ${monitorMetric("Completed/min", completed, completedNote)}
        ${monitorMetric("Resources", resourcesLabel(runtime), runtime ? `sample ${formatInteger(runtime.performance.sampleSize)} · pages ${formatInteger(runtime.browser.pages)}` : UNMEASURED)}
      </div>
      ${issueHtml}
      <p class="ecourts-monitor-history">${escapeHtml(historyLabel(health, runtime))}</p>
      <p class="ecourts-monitor-provenance">${escapeHtml(provenanceLabel(health, runtime))}</p>
      <div class="ecourts-monitor-cases">
        <h3>Active cases</h3>
        ${activeHtml}
      </div>
      <p class="ecourts-monitor-sampled">Sampled ${health?.sampledAt ? escapeHtml(datedWita(health.sampledAt)) : UNMEASURED}</p>
      </div>
    </details>
  `;
}

function monitorMetric(label: string, value: string, note: string): string {
  return `<div class="ecourts-monitor-metric"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong><small>${escapeHtml(note)}</small></div>`;
}

function monitorIssues(health: EcourtsHealth | null | undefined): string[] {
  if (!health) return ["health_unavailable"];
  const issues = [...health.alerts];
  if (!health.runtime) issues.push("runtime_unavailable");
  else issues.push(...runtimeIssues(health.runtime));
  return unique(issues);
}

function monitorTone(status: EcourtsRuntimeStatus | undefined, quiet: boolean): "online" | "offline" {
  const healthy = status === "idle" || status === "working" || status === "warming" || status === "starting";
  return healthy && quiet ? "online" : "offline";
}

function statusCopy(runtime: EcourtsRuntimeMonitor, issues: string[]): string {
  const check = issues.length ? " · check" : " · okay";
  if (runtime.status === "warming" || runtime.status === "starting") return `Starting${check}`;
  if (runtime.status === "working") return `Solving${check}`;
  if (runtime.status === "retrying") return "Retrying · check";
  if (runtime.status === "idle") {
    return runtime.browser.connected ? `Idle${check}` : `Idle · browser closed${check}`;
  }
  return runtime.status === "unavailable" ? "Unavailable" : "Degraded";
}

function currentFaultStatus(status: EcourtsRuntimeStatus): boolean {
  return status !== "idle";
}

function runtimeIssues(runtime: EcourtsRuntimeMonitor): string[] {
  const issues = [...runtime.alerts];
  const idleOrStarting = runtime.status === "idle" || runtime.status === "starting";
  if (!runtime.browser.connected && !idleOrStarting) issues.push("browser_disconnected");
  if (runtime.browser.extensionRequired !== false && runtime.browser.extensionInstalled === false) {
    issues.push("extension_unavailable");
  }
  if (runtime.lastError && currentFaultStatus(runtime.status)) {
    const kind = /extension|classifier|browser/i.test(runtime.lastError) ? "extension_error" : "runtime_error";
    issues.push(`${kind}:${runtime.lastError}`);
  }
  if (runtime.resources.cpuPercent == null || runtime.resources.memoryBytes == null) issues.push("system_resources_unmeasured");
  for (const attempt of runtime.active) {
    if (attempt.elapsedMs >= ECOURTS_LOOKUP_DEADLINE_MS) issues.push(`lookup_deadline:${attempt.caseNumber || attempt.runId}`);
    else if (attempt.progressAgeMs >= 20_000) issues.push(`stalled:${attempt.caseNumber || attempt.runId}`);
  }
  return unique(issues);
}

function issueCopy(issue: string): string {
  if (issue === "health_unavailable") return "Runtime health unavailable; all measurements are unmeasured.";
  if (issue === "runtime_unavailable") return "Runtime monitor has not published a snapshot; measurements are unmeasured.";
  if (issue === "extension_unavailable") return "Custom eCourts extension is missing on disk.";
  if (issue === "extension_not_loaded") return "Extension is installed but not loaded in the current browser session.";
  if (issue === "browser_disconnected") return "Browser session is disconnected.";
  if (issue === "startup_retrying") return "Worker is retrying a failed browser startup. Empty queue does not mean healthy.";
  if (issue === "queue_stalled") return "Queued eCourts work is older than two lookup envelopes and nothing is claiming.";
  if (issue === "concurrency_over_cap") return `Running eCourts claims exceed the ${ECOURTS_MAX_CONCURRENCY}-slot cap.`;
  if (issue === "system_resources_unmeasured") return "CPU or memory measurement is unavailable.";
  if (issue === "concurrency_over_limit") return `Active lookups exceed the ${ECOURTS_MAX_CONCURRENCY}-slot limit.`;
  if (issue === "lookup_failures_recent") return "Recent eCourts lookups failed; inspect the source or browser session.";
  if (issue === "lookup_timeouts_recent") return "Recent eCourts lookups timed out; inspect the source or browser session.";
  if (issue === "performance_target_missed") return `Fewer than 80% of recent lookups met the ${formatInteger(ECOURTS_LOOKUP_TARGET_MS / 1000)}s target.`;
  if (issue.startsWith("stalled:")) return `No progress for ${issue.slice("stalled:".length)}.`;
  if (issue.startsWith("lookup_deadline:")) return `${issue.slice("lookup_deadline:".length)} reached the 60s lookup cap.`;
  if (issue.startsWith("extension_error:")) return `Extension error: ${issue.slice("extension_error:".length)}`;
  if (issue.startsWith("runtime_error:")) return `Runtime error: ${issue.slice("runtime_error:".length)}`;
  return issue.replaceAll("_", " ");
}

function recoveryHtml(issues: string[], runtime: EcourtsRuntimeMonitor | null): string {
  const action = recoveryAction(issues, runtime);
  return action ? `<p class="ecourts-monitor-recovery">Next: ${escapeHtml(action)}</p>` : "";
}

function recoveryAction(issues: string[], runtime: EcourtsRuntimeMonitor | null): string | null {
  if (issues.includes("startup_retrying") || runtime?.status === "retrying") {
    return "Wait for the next startup retry; inspect the dated last failure if it repeats.";
  }
  if (issues.includes("queue_stalled")) return "Inspect queued eCourts claims; the worker may be idle or retrying startup.";
  if (issues.includes("concurrency_over_cap")) return `Drain extra running claims down to ${ECOURTS_MAX_CONCURRENCY}.`;
  if (issues.includes("extension_unavailable")) return "Install the eCourts extension on the VPS host.";
  if (issues.includes("browser_disconnected")) return "Inspect Chromium startup on the VPS.";
  if (issues.some((issue) => issue.startsWith("runtime_error:") || issue.startsWith("extension_error:"))) {
    return "Last failure stays until a successful lookup; treat it as current only while retrying or degraded.";
  }
  return null;
}

function browserLabel(runtime: EcourtsRuntimeMonitor | null): string {
  if (!runtime) return UNMEASURED;
  if (runtime.browser.connected) return "connected";
  if (runtime.status === "starting") return "starting";
  if (runtime.status === "idle") return "idle closed";
  if (runtime.status === "retrying") return "disconnected · retrying";
  return "disconnected";
}

function extensionLabel(runtime: EcourtsRuntimeMonitor | null): string {
  if (!runtime) return UNMEASURED;
  const required = runtime.browser.extensionRequired !== false;
  const installed = runtime.browser.extensionInstalled;
  const install = !required
    ? "extension not used"
    : installed === true
      ? "extension installed"
      : installed === false
        ? "extension missing on disk"
        : "extension install unmeasured";
  const loaded = runtime.browser.connected
    ? `loaded ${readinessLabel(runtime.browser.extensionLoaded)}`
    : "not loaded (no session)";
  const classifier = runtime.browser.connected
    ? `classifier ${runtime.browser.classifierReady ? "ready" : "not ready"}`
    : "classifier unmeasured (no session)";
  return `${install} · ${loaded} · ${classifier}`;
}

function readinessLabel(value: boolean): string {
  return value ? "yes" : "no";
}

function rollingWindowNote(runtime: EcourtsRuntimeMonitor | null): string {
  if (!runtime) return UNMEASURED;
  const windowMs = runtime.performance.windowMs ?? DEFAULT_WINDOW_MS;
  const maxSamples = runtime.performance.maxSamples ?? DEFAULT_MAX_SAMPLES;
  const minutes = Math.round(windowMs / 60_000);
  const samples = runtime.performance.sampleSize;
  return `${formatInteger(samples)}/${formatInteger(maxSamples)} samples in last ${formatInteger(minutes)} min (resets with process)`;
}

function historyLabel(health: EcourtsHealth | null | undefined, runtime: EcourtsRuntimeMonitor | null): string {
  const successAt = health?.lastSuccessAt ?? runtime?.lastSuccessAt ?? null;
  const failureAt = health?.lastFailureAt ?? runtime?.lastErrorAt ?? null;
  const failureClass = health?.lastFailureClass ?? runtime?.lastError ?? null;
  const current = runtime?.lastError && currentFaultStatus(runtime.status)
    ? `Current fault ${runtime.lastError} at ${datedWita(runtime.lastErrorAt)}.`
    : "No current fault.";
  const recovered = runtime?.lastSuccessAt && runtime.lastErrorAt && runtime.lastSuccessAt > (runtime.lastErrorAt ?? "")
    ? " Recovered after last process failure."
    : "";
  return `Last success ${datedWita(successAt)} · Last failure ${datedWita(failureAt)}${failureClass ? ` (${failureClass})` : ""} · ${current}${recovered}`;
}

function provenanceLabel(health: EcourtsHealth | null | undefined, runtime: EcourtsRuntimeMonitor | null): string {
  const revision = health?.releaseRevision?.trim() || UNMEASURED;
  const started = runtime?.performance.processStartedAt ? datedWita(runtime.performance.processStartedAt) : UNMEASURED;
  return `Lookups run on VPS · ledger SQLite · release ${revision} · process started ${started}. Cloudflare/R2 are not the browser execution backend.`;
}

function resourcesLabel(runtime: EcourtsRuntimeMonitor | null): string {
  if (!runtime) return UNMEASURED;
  const host = runtime.resources.cpuCount != null
    ? `${formatInteger(runtime.resources.cpuCount)}-CPU host`
    : "host CPU count unmeasured";
  const memory = runtime.resources.memoryLimitBytes == null
    ? `memory ${bytes(runtime.resources.memoryBytes)} cgroup`
    : `memory ${bytes(runtime.resources.memoryBytes)} / ${bytes(runtime.resources.memoryLimitBytes)} cgroup`;
  return `CPU ${percent(runtime.resources.cpuPercent)} of one CPU (${host}) · ${memory} · host free ${bytes(runtime.resources.hostAvailableMemoryBytes)} · loop ${durationMs(runtime.resources.eventLoopDelayMs)}`;
}

function datedWita(value: string | null | undefined): string {
  if (!value) return UNMEASURED;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const formatted = new Intl.DateTimeFormat("en-GB", {
    timeZone: MONITOR_TIMEZONE,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(date);
  return `${formatted} WITA`;
}

function durationMs(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return UNMEASURED;
  if (value < 1_000) return `${Math.round(value)}ms`;
  return `${formatNumber(value / 1_000, 1)}s`;
}

function percent(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? UNMEASURED : `${formatNumber(value, 1)}%`;
}

function bytes(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return UNMEASURED;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)}KB`;
  return `${formatNumber(value / (1024 * 1024), 1)}MB`;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}
